import { useState, type FormEvent } from "react";
import { PageHero } from "@/components/PageHero";
import { Seo } from "@/components/Seo";
import { useReveals } from "@/lib/motion";
import { CONTACT_EMAIL, DOCS_URL } from "@/lib/site";

interface Message {
  name: string;
  email: string;
  company: string;
  message: string;
}

const EMPTY: Message = { name: "", email: "", company: "", message: "" };

/** The site has no backend of its own: the form prepares an email in the visitor's mail app. */
function buildMailtoUrl({ name, email, company, message }: Message): string {
  const subject = `MineOptics enquiry from ${name}`;
  const lines = [`Name: ${name}`, `Email: ${email}`];
  if (company) lines.push(`Company: ${company}`);
  const body = [...lines, "", message].join("\n");
  return `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

const fieldClass =
  "w-full border-0 border-b border-fg/25 bg-transparent px-0 py-3 text-lg outline-none transition-colors duration-300 placeholder:text-fg/30 focus:border-fg focus-visible:outline-none";
const labelClass = "eyebrow block text-mute";

export default function Contact() {
  const scope = useReveals();
  const [form, setForm] = useState<Message>(EMPTY);
  const [opened, setOpened] = useState(false);

  const set = (key: keyof Message) => (event: { target: { value: string } }) =>
    setForm((current) => ({ ...current, [key]: event.target.value }));

  const submit = (event: FormEvent) => {
    event.preventDefault();
    window.location.href = buildMailtoUrl(form);
    setOpened(true);
  };

  return (
    <div ref={scope}>
      <Seo title="Contact" description="Questions about MineOptics? Email hello@mineoptics.com." path="/contact" />
      <PageHero
        eyebrow="Contact"
        title="Talk to us."
        lede="Questions about the product, a plan, or whether MineOptics fits your study."
      />

      <section data-theme="light" className="bg-bg">
        <div className="shell grid gap-16 py-24 md:py-36 lg:grid-cols-[1fr_1.4fr]">
          <div data-fade>
            <p className="eyebrow text-mute">Email</p>
            <a
              href={`mailto:${CONTACT_EMAIL}`}
              className="subtitle mt-4 inline-block underline decoration-fg/25 underline-offset-8 transition-colors duration-300 hover:decoration-fg"
            >
              {CONTACT_EMAIL}
            </a>
            <p className="eyebrow mt-14 text-mute">Looking for how-to?</p>
            <a
              href={DOCS_URL}
              className="subtitle mt-4 inline-block underline decoration-fg/25 underline-offset-8 transition-colors duration-300 hover:decoration-fg"
            >
              Documentation
            </a>
          </div>

          <form data-fade onSubmit={submit} className="space-y-9">
            <div className="grid gap-9 md:grid-cols-2">
              <div>
                <label htmlFor="name" className={labelClass}>
                  Name
                </label>
                <input id="name" type="text" required autoComplete="name" value={form.name} onChange={set("name")} className={fieldClass} />
              </div>
              <div>
                <label htmlFor="email" className={labelClass}>
                  Email
                </label>
                <input id="email" type="email" required autoComplete="email" value={form.email} onChange={set("email")} className={fieldClass} />
              </div>
            </div>
            <div>
              <label htmlFor="company" className={labelClass}>
                Company <span className="normal-case tracking-normal text-fg/40">(optional)</span>
              </label>
              <input id="company" type="text" autoComplete="organization" value={form.company} onChange={set("company")} className={fieldClass} />
            </div>
            <div>
              <label htmlFor="message" className={labelClass}>
                Message
              </label>
              <textarea id="message" required rows={5} value={form.message} onChange={set("message")} className={`${fieldClass} resize-y`} />
            </div>

            <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
              <button
                type="submit"
                className="group inline-flex items-center justify-center rounded-full bg-fg px-7 py-4 text-[0.9375rem] font-medium text-bg transition-colors duration-300 hover:bg-fg/90"
              >
                <span className="roll">
                  <span data-text="Compose email">Compose email</span>
                </span>
              </button>
              <p className="text-sm text-mute">Opens your mail app with the message ready to send.</p>
            </div>

            {opened && (
              <p role="status" className="max-w-[60ch] text-mute">
                Your mail app should have opened with the message filled in. Nothing is sent until you send it from
                there. If nothing opened, write to{" "}
                <a href={`mailto:${CONTACT_EMAIL}`} className="text-fg underline underline-offset-4">
                  {CONTACT_EMAIL}
                </a>{" "}
                directly.
              </p>
            )}
          </form>
        </div>
      </section>
    </div>
  );
}
